import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Input,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Typography,
    message,
    Pagination,
    Modal,
    Select,
    InputNumber,
    Tooltip,
    ConfigProvider,
    theme,
    Tag
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EditOutlined,
    FilterOutlined,
    ClearOutlined,
    CaretUpOutlined,
    CaretDownOutlined,
    EyeOutlined,
    PlusOutlined,
    MoreOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, isDateField, applyAdvancedFilter, redirectToPivotWithData, exportToExcel, printTable } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import FormikForm from '../form/FormikForm';
import '../insaat/KurulumHareketleriPage.css';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const iconMap = {
    'EyeOutlined': EyeOutlined,
    'EditOutlined': EditOutlined,
    'DeleteOutlined': DeleteOutlined,
    'PlusOutlined': PlusOutlined,
    'MoreOutlined': MoreOutlined,
};

const ActionButtons = ({ record, actionButtons, darkMode }) => {
    if (!actionButtons || actionButtons.length === 0) {
        return null;
    }

    if (actionButtons.length > 2) {
        const menuItems = actionButtons.map(button => {
            const IconComponent = iconMap[button.icon];
            return {
                key: button.key,
                label: button.label,
                icon: IconComponent ? <IconComponent /> : null,
                onClick: () => button.onClick(record)
            };
        });

        return (
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
            >
                <Button
                    type="text"
                    icon={<MoreOutlined />}
                    size="small"
                    className={darkMode ? 'dark-button' : ''}
                    style={{
                        color: darkMode ? 'var(--dark-text)' : undefined
                    }}
                />
            </Dropdown>
        );
    }

    return (
        <Space size="small">
            {actionButtons.map(button => {
                const IconComponent = iconMap[button.icon];
                return (
                    <Button
                        key={button.key}
                        type={button.type}
                        size="small"
                        icon={IconComponent ? <IconComponent /> : null}
                        onClick={() => button.onClick(record)}
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                            color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                    >
                        {button.label}
                    </Button>
                );
            })}
        </Space>
    );
};

const ToplantiPage = () => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [filters, setFilters] = useState({
        dateRange: null,
        search: '',
        page: 1,
        pageSize: 50,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const toplantiData = {
        key: 'toplanti',
        label: 'Toplantılar',
        sqlKey: 'toplanti',
        actionButtons: {
            lastColumn: [
                {
                    key: 'edit',
                    label: 'Rapor',
                    type: 'default',
                    icon: 'EditOutlined',
                    onClick: (record) => {
                        const id = record.key_toplantı;
                        if (id) {
                            //pdf gösterme
                        } else {
                            message.error('ID bulunamadı');
                        }
                    }
                },
                {
                    key: 'view',
                    label: 'Yönetim',
                    type: 'default',
                    icon: 'EyeOutlined',
                    onClick: (record) => {
                        const id = record.key_toplantı;
                        if (id) {
                            //yönetim sayfasına yönlendirme
                        } else {
                            message.error('ID bulunamadı');
                        }
                    }
                }
            ]
        }
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters({
            dateRange: null,
            search: '',
            page: 1,
            pageSize: 50
        });
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        let dateA = null, dateB = null;
                        
                        if (valA) {
                            const dateValueA = String(valA).trim();
                            if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                            } else if (dateValueA.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD-MM-YYYY');
                            } else {
                                dateA = dayjs(valA);
                            }
                        }
                        
                        if (valB) {
                            const dateValueB = String(valB).trim();
                            if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                            } else if (dateValueB.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD-MM-YYYY');
                            } else {
                                dateB = dayjs(valB);
                            }
                        }
                        
                        if (!dateA && !dateB) return 0;
                        if (!dateA || !dateA.isValid()) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB || !dateB.isValid()) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const paginatedData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        return filteredAndSortedData.slice(startIndex, startIndex + pagination.pageSize);
    }, [filteredAndSortedData, pagination.current, pagination.pageSize]);

    const getSortIcon = (columnKey) => {
        if (sortedInfo.columnKey === columnKey) {
            return sortedInfo.order === "asc" ? <CaretUpOutlined /> : <CaretDownOutlined />;
        }
        return null;
    };

    const removeColumn = (columnKey) => {
        setColumns((prevCols) => prevCols.filter((col) => col.key !== columnKey));
    };

    const getFilterDropdown = (column) => {
        const currentFilter = filteredInfo[column.key] || {};
        const condition = currentFilter.condition || "choose";

        return (
            <div style={{ padding: 16, minWidth: 300 }}>
                <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg)' : 'white', border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`, padding: 12 }}>
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                            Filtre Şartı
                        </Text>
                        <Select
                            size="small"
                            value={condition}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                            style={{ width: '100%' }}
                        >
                            {column.filters.map((filter) => (
                                <Option key={filter.value} value={filter.value}>
                                    {filter.text}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* String değer seçimi */}
                    {column.isString && condition === "select" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer Seç
                            </Text>
                            <Select
                                size="small"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                placeholder="Seçiniz"
                            >
                                <Option value="">Seçiniz</Option>
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {/* Sayısal filtreler için input alanları */}
                    {column.isNumber && condition !== "choose" && condition !== "between" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Sayısal arasında filtresi */}
                    {column.isNumber && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Min Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Min"
                                    value={currentFilter.min || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Max Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Max"
                                    value={currentFilter.max || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* String filtreler için input alanları */}
                    {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <Input
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Tarih filtreler için input alanları */}
                    {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Tarih
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.value ? dayjs(currentFilter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                            />
                        </div>
                    )}

                    {/* Tarih arasında filtresi */}
                    {column.isDateColumn && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Başlangıç Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.startDate ? dayjs(currentFilter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Bitiş Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.endDate ? dayjs(currentFilter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                />
                            </div>
                        </>
                    )}

                    <Button
                        size="small"
                        onClick={() => clearColumnFilter(column.key)}
                        style={{ marginTop: 8, width: '100%' }}
                    >
                        Filtreyi Sıfırla
                    </Button>
                </Space>
            </div>
        );
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let url, response;

            url = `${API_BASE_URL}/list/execute/${toplantiData.sqlKey}`;
            response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }

            let filteredData = [...allData];

            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                const filterStartDate = filters.dateRange[0].startOf('day');
                const filterEndDate = filters.dateRange[1].endOf('day');
                
                filteredData = filteredData.filter(record => {
                    const tarih = record['Tarih'] || record['tarih'];
                    let recordDate;
                    const dateValue = String(tarih).trim();

                    if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD.MM.YYYY');
                    } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY.MM.DD');
                    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY-MM-DD');
                    } else if (dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD-MM-YYYY');
                    } else {
                        recordDate = dayjs(tarih);
                    }

                    if (recordDate.isValid()) {
                        const isInRange = recordDate.isBetween(filterStartDate, filterEndDate, null, '[]');
                        return isInRange;
                    }
                    
                    return false;
                });
            }

            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }

            if (filteredData.length > 0) {
                const sample = filteredData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => 
                        !key.toLowerCase().includes('fatura') && 
                        !key.toLowerCase().includes('türü') &&
                        !key.toLowerCase().includes('key_depo') &&
                        !key.toLowerCase().includes('key_kabin_islem')
                    )
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = isDateField(key);
                        const isNumber = (!isDateColumn && (
                                        (typeof firstValue === "number" && !isNaN(firstValue)) ||
                                        (typeof firstValue === "string" && (
                                            firstValue.match(/^₺?[\d.,]+$/) ||
                                            firstValue.match(/^\$?[\d.,]+$/) ||
                                            firstValue.match(/^€?[\d.,]+$/) ||
                                            firstValue.match(/^[\d.,]+$/) ||
                                            key.toLowerCase().includes('tutar') ||
                                            key.toLowerCase().includes('fiyat') ||
                                            key.toLowerCase().includes('ücret') ||
                                            key.toLowerCase().includes('miktar') ||
                                            key.toLowerCase().includes('adet') ||
                                            key.toLowerCase().includes('kesilen') ||
                                            key.toLowerCase().includes('toplam')
                                        )))
                        );
                        const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters,
                        };
                    });
                setColumns(dynamicCols);
            }

            setData(filteredData || []);
            setShowData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));

        } catch (err) {
            console.error('Toplantılar data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [filters.search, filters.dateRange]);

    const handlePivotRedirect = useCallback(() => {
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: toplantiData.label || 'Toplantılar',
            sqlKey: toplantiData.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
        
        if (key === 'dateRange') {
            setTimeout(() => {
                loadData();
            }, 100);
        }
        
        if (key === 'search') {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            
            const newTimeout = setTimeout(() => {
                loadData();
            }, 500); 
            
            setSearchTimeout(newTimeout);
        }
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    return (
        <div className={`kurulum-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`kurulum-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                {/* Başlık*/}
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Title level={3} style={{ 
                            color: darkMode ? 'var(--dark-text)' : undefined,
                            margin: 0,
                            textAlign: 'center'
                        }}>
                            {toplantiData.label}
                            <EditOutlined onClick={() => handleOpenForm(275, 'Liste Formu', 'sqls', 'id', 45)} />
                        </Title>
                    </Col>
                </Row>

                {/* Filtreler */}
                <Row gutter={[16, 16]} align="middle">
                    {/* Tarih Aralığı */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Tarih Aralığı
                            </Text>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(dates) => handleFilterChange('dateRange', dates)}
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                placeholder={['Başlangıç', 'Bitiş']}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                                locale={{
                                    lang: {
                                        locale: 'tr_TR',
                                        placeholder: 'Tarih seçin',
                                        rangePlaceholder: ['Başlangıç', 'Bitiş'],
                                        today: 'Bugün',
                                        now: 'Şimdi',
                                        backToToday: 'Bugüne dön',
                                        ok: 'Tamam',
                                        clear: 'Temizle',
                                        month: 'Ay',
                                        year: 'Yıl',
                                        timeSelect: 'Zaman seç',
                                        dateSelect: 'Tarih seç',
                                        monthSelect: 'Ay seç',
                                        yearSelect: 'Yıl seç',
                                        decadeSelect: 'On yıl seç',
                                        yearFormat: 'YYYY',
                                        dateFormat: 'DD.MM.YYYY',
                                        dayFormat: 'DD',
                                        dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
                                        monthFormat: 'MMMM',
                                        monthBeforeYear: true,
                                        previousMonth: 'Önceki ay (PageUp)',
                                        nextMonth: 'Sonraki ay (PageDown)',
                                        previousYear: 'Önceki yıl (Control + left)',
                                        nextYear: 'Sonraki yıl (Control + right)',
                                        previousDecade: 'Önceki on yıl',
                                        nextDecade: 'Sonraki on yıl',
                                        previousCentury: 'Önceki yüzyıl',
                                        nextCentury: 'Sonraki yüzyıl',
                                    },
                                    timePickerLocale: {
                                        placeholder: 'Zaman seçin',
                                    },
                                }}
                            />
                        </div>
                    </Col>

                    {/* Arama*/}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları*/}
                    <Col xs={24} sm={24} md={12} lg={16}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    icon={<ClearOutlined />}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: toplantiData?.label || 'Toplantılar', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tablo */}
            <Card
                className={`kurulum-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="kurulum-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={handlePivotRedirect}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>

                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={paginatedData}
                        loading={loading}
                        rowKey={(record) => record['Platform No'] || Math.random()}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `Toplam ${total} kayıt (${range[0]}-${range[1]})`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={columns.map((column, index) => {
                            const getColumnWidth = (key) => {
                                const keyLength = key.length;

                                if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                    return Math.max(120, keyLength * 8);
                                }
                                if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                    return Math.max(120, keyLength * 8);
                                }
                                if (keyLength > 20) {
                                    return Math.max(200, keyLength * 8);
                                }
                                if (keyLength > 10) {
                                    return Math.max(120, keyLength * 8);
                                }
                                return Math.max(80, keyLength * 8);
                            };

                            const totalColumns = columns.length;
                            const isLastColumn = index === totalColumns - 1;
                            const hasActionButtons = toplantiData && toplantiData.actionButtons;
                            const hasLastButtons = hasActionButtons && toplantiData.actionButtons.lastColumn && toplantiData.actionButtons.lastColumn.length > 0;

                            const shouldShowActionButtons = isLastColumn && hasLastButtons;
                            const actionButtonsToShow = hasActionButtons ? (toplantiData.actionButtons?.lastColumn || []) : [];

                            return {
                                title: (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <Tooltip title="Sütunu Kaldır">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ClearOutlined />}
                                                onClick={() => removeColumn(column.key)}
                                                style={{
                                                    color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                                    padding: '2px 4px',
                                                    minWidth: 'auto',
                                                    height: 'auto'
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                            />
                                        </Tooltip>
                                        <span style={{ flex: 1 }}>{column.title}</span>
                                        {column.sortable && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={getSortIcon(column.key)}
                                                onClick={() => handleSort(column.key)}
                                                style={{
                                                    color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                                    padding: '2px 4px',
                                                    minWidth: 'auto',
                                                    height: 'auto'
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                            />
                                        )}
                                        <Dropdown
                                            dropdownRender={() => getFilterDropdown(column)}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                style={{
                                                    marginLeft: 4,
                                                    color: filteredInfo[column.key] ? (darkMode ? 'var(--dark-text)' : '#1890ff') : '#ccc'
                                                }}
                                            >
                                                <FilterOutlined style={{ fontSize: 12 }} />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ),
                                dataIndex: column.key,
                                key: column.key,
                                align: 'center',
                                width: shouldShowActionButtons ? Math.max(getColumnWidth(column.key), 200) : getColumnWidth(column.key),
                                ellipsis: {
                                    showTitle: true
                                },
                                render: (text, record) => {
                                    if (column.key.toLowerCase().includes('katılımcı') || 
                                        column.key.toLowerCase().includes('participant')) {
                                        if (!text || text === '-') {
                                            return <span>-</span>;
                                        }
                                        
                                        let participants = [];
                                        if (typeof text === 'string') {
                                            participants = text
                                                .split(/[,;\n]/)
                                                .map(p => p.trim())
                                                .filter(p => p.length > 0);
                                        } else if (Array.isArray(text)) {
                                            participants = text.filter(p => p);
                                        } else {
                                            participants = [String(text)];
                                        }
                                        
                                        if (participants.length === 0) {
                                            return <span>-</span>;
                                        }
                                        
                                        const participantCount = participants.length;
                                        const participantList = participants.join(', ');
                                        return (
                                            <Tooltip 
                                                title={
                                                    <div style={{ maxWidth: '300px' }}>
                                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                            Katılımcılar ({participantCount}):
                                                        </div>
                                                        <div style={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column',
                                                            gap: '4px'
                                                        }}>
                                                            {participants.map((participant, idx) => (
                                                                <div key={idx} style={{ fontSize: '12px' }}>
                                                                    • {participant}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                }
                                                placement="top"
                                                overlayStyle={{ maxWidth: '350px' }}
                                            >
                                                <span style={{ 
                                                    cursor: 'pointer',
                                                    color: darkMode ? 'var(--dark-text)' : '#1890ff',
                                                    fontWeight: 500,
                                                    textDecoration: 'underline',
                                                    textDecorationStyle: 'dotted'
                                                }}>
                                                    {participantCount} {participantCount === 1 ? 'Kişi' : 'Kişi'}
                                                </span>
                                            </Tooltip>
                                        );
                                    }
                                    
                                    if (column.key.toLowerCase().includes('gündem') || 
                                        column.key.toLowerCase().includes('agenda') ||
                                        column.key.toLowerCase().includes('madde')) {
                                        if (!text || text === '-') {
                                            return <span>-</span>;
                                        }
                                        
                                        let agendaItems = [];
                                        if (typeof text === 'string') {
                                            agendaItems = text
                                                .split(/[,;\n]/)
                                                .map(item => item.trim())
                                                .filter(item => item.length > 0);
                                        } else if (Array.isArray(text)) {
                                            agendaItems = text.filter(item => item);
                                        } else {
                                            agendaItems = [String(text)];
                                        }
                                        
                                        if (agendaItems.length === 0) {
                                            return <span>-</span>;
                                        }
                                        
                                        const agendaCount = agendaItems.length;
                                        return (
                                            <span>
                                                {agendaCount} {agendaCount === 1 ? 'madde' : 'madde'}
                                            </span>
                                        );
                                    }
                                    
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {shouldShowActionButtons ? (
                                                <ActionButtons
                                                    record={record}
                                                    actionButtons={actionButtonsToShow}
                                                    darkMode={darkMode}
                                                />
                                            ) : (
                                                <span>{formatCellValue(column.key, text)}</span>
                                            )}
                                        </div>
                                    );
                                },
                                sorter: column.sortable ? (a, b) => {
                                    const aVal = a[column.key];
                                    const bVal = b[column.key];
                                    if (column.isNumber) {
                                        const numA = Number(aVal) || 0;
                                        const numB = Number(bVal) || 0;
                                        return numA - numB;
                                    } else if (column.isDateColumn) {
                                        let dateA = null, dateB = null;
                                        
                                        if (aVal) {
                                            const dateValueA = String(aVal).trim();
                                            if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                                            } else if (dateValueA.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                                dateA = dayjs(dateValueA, 'DD-MM-YYYY');
                                            } else {
                                                dateA = dayjs(aVal);
                                            }
                                        }
                                        
                                        if (bVal) {
                                            const dateValueB = String(bVal).trim();
                                            if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                                            } else if (dateValueB.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                                dateB = dayjs(dateValueB, 'DD-MM-YYYY');
                                            } else {
                                                dateB = dayjs(bVal);
                                            }
                                        }
                                        
                                        if (!dateA && !dateB) return 0;
                                        if (!dateA || !dateA.isValid()) return -1;
                                        if (!dateB || !dateB.isValid()) return 1;
                                        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                                    } else {
                                        return String(aVal || "").localeCompare(String(bVal || ""), 'tr');
                                    }
                                } : false
                            };
                        })}
                    />
                </div>
            </Card>

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

        </div>
    );
};

export default ToplantiPage;